import { ReactNode, useMemo, useState } from 'react';
import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconFileText,
  IconPaperclip,
  IconPhoto,
  IconRobot,
  IconThumbDown,
  IconThumbUp,
  IconUser,
} from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import { getButtonSelectionOptions } from '../../ConversationTranscript/buttonSelectionOptions';
import { EmbedStep } from '../../ConversationTranscript/EmbedStep';
import { ImageStep } from '../../ConversationTranscript/ImageStep';
import {
  ButtonSelectionTranscriptComponent,
  ConversationAttachment,
  CsatSubmissionTranscriptComponent,
  EmbedTranscriptComponent,
  ImageTranscriptComponent,
  QuickFeedbackTranscriptComponent,
  TextTranscriptComponent,
  TranscriptComponent,
} from '../../ConversationTranscript/types';
import { safeLinkifyHtml } from '../../ConversationTranscript/utils';
import { formatEventTime } from './auditData';

const ButtonSelectionAnswer = ({
  component,
}: {
  component: ButtonSelectionTranscriptComponent;
}) => {
  const { palette } = useTheme();
  const options = getButtonSelectionOptions(component);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {options.map(option => {
        const isSelected = option.value === component.option_selected;
        return (
          <Box
            key={option.value}
            sx={{
              alignItems: 'center',
              bgcolor: isSelected
                ? palette.colors.grey[100]
                : palette.colors.white,
              border: `1px solid ${
                isSelected
                  ? palette.colors.grey[600]
                  : palette.colors.slate[200]
              }`,
              borderRadius: '16px',
              display: 'flex',
              gap: '4px',
              px: '12px',
              py: '6px',
            }}
          >
            {isSelected && (
              <IconCheck color={palette.colors.grey[700]} size={14} />
            )}
            <Typography
              color={
                isSelected ? palette.colors.grey[800] : palette.colors.grey[600]
              }
              variant={isSelected ? 'font14Medium' : 'font14'}
            >
              {option.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const CsatAnswer = ({
  component,
}: {
  component: CsatSubmissionTranscriptComponent;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const fallback = t('csatSubmissionStep.fallbackValue');
  const resolved =
    component.csat_resolve === undefined || component.csat_resolve === null
      ? fallback
      : component.csat_resolve
        ? t('conversationAudit.csatResolvedYes')
        : t('conversationAudit.csatResolvedNo');
  const fields = [
    {
      label: t('csatSubmissionStep.csatScore'),
      value: String(component.csat_score ?? ''),
    },
    {
      label: t('csatSubmissionStep.reasonsForRating'),
      value: component.csat_feedback?.join(', '),
    },
    {
      label: t('csatSubmissionStep.additionalFeedback'),
      value: component.additional_feedback,
    },
    {
      label: t('csatSubmissionStep.resolutionConfirmation'),
      value: resolved,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {fields.map(({ label, value }) => (
        <Box
          key={label}
          sx={{ columnGap: '4px', display: 'flex', flexWrap: 'wrap' }}
        >
          <Typography variant='font14Bold'>
            {t('common.labelWithColon', { label })}
          </Typography>
          <Typography variant='font14'>{value || fallback}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const QuickFeedbackAnswer = ({
  component,
}: {
  component: QuickFeedbackTranscriptComponent;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const Icon = component.is_useful ? IconThumbUp : IconThumbDown;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: '6px' }}>
        <Icon color={palette.colors.grey[700]} size={14} />
        <Typography variant='font14'>
          {component.is_useful
            ? t('conversationAudit.quickFeedbackHelpful')
            : t('conversationAudit.quickFeedbackNotHelpful')}
        </Typography>
      </Box>
      {component.additional_feedback && (
        <Typography variant='font14'>
          {t('conversationAudit.fieldWithValue', {
            label: t('csatSubmissionStep.additionalFeedback'),
            value: component.additional_feedback,
          })}
        </Typography>
      )}
    </Box>
  );
};

const attachmentIcon = (fileType?: string | null) => {
  if (fileType?.startsWith('image/')) {
    return IconPhoto;
  }
  if (fileType === 'application/pdf' || fileType?.startsWith('text/')) {
    return IconFileText;
  }
  return IconPaperclip;
};

// Per-file upload summaries ride on the query transcript component; the
// bubble's "(Uploaded N attachments)" suffix only counts them. They start
// collapsed behind a chip so long summaries don't flood the transcript.
const AttachmentSummaries = ({
  attachments,
  isAgent,
}: {
  attachments: ConversationAttachment[];
  isAgent: boolean;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box
      sx={{
        alignItems: isAgent ? 'flex-start' : 'flex-end',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        // Offset the avatar column so the block lines up with the bubble.
        ...(isAgent ? { ml: '32px' } : { mr: '32px' }),
      }}
    >
      <Box
        aria-expanded={isExpanded}
        component='button'
        onClick={() => setIsExpanded(previous => !previous)}
        sx={{
          '&:hover': { bgcolor: palette.colors.grey[100] },
          alignItems: 'center',
          bgcolor: palette.colors.white,
          border: `1px solid ${palette.colors.slate[200]}`,
          borderRadius: '16px',
          cursor: 'pointer',
          display: 'flex',
          font: 'inherit',
          gap: '4px',
          px: '12px',
          py: '6px',
        }}
      >
        <IconPaperclip color={palette.colors.grey[600]} size={14} />
        <Typography variant='font12Medium'>
          {t('conversationAudit.attachmentCount', {
            count: attachments.length,
          })}
        </Typography>
        {isExpanded ? (
          <IconChevronUp color={palette.colors.grey[600]} size={14} />
        ) : (
          <IconChevronDown color={palette.colors.grey[600]} size={14} />
        )}
      </Box>
      {isExpanded && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            width: '100%',
          }}
        >
          {attachments.map((attachment, index) => {
            if (!attachment.file_name && !attachment.summary) {
              return null;
            }
            const Icon = attachmentIcon(attachment.file_type);
            return (
              <Box
                key={attachment.file_name ?? index}
                sx={{
                  border: `1px solid ${palette.colors.slate[200]}`,
                  borderRadius: '16px',
                  px: '14px',
                  py: '10px',
                }}
              >
                {attachment.file_name && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: '4px',
                    }}
                  >
                    <Icon color={palette.colors.grey[600]} size={14} />
                    <Typography
                      color={palette.colors.grey[600]}
                      variant='font12Medium'
                    >
                      {attachment.file_name}
                    </Typography>
                  </Box>
                )}
                {attachment.summary && (
                  <Box sx={{ whiteSpace: 'pre-wrap' }}>
                    <Typography variant='font12'>
                      {attachment.summary}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// Speaker, time and bubble in one block. Unlike the shared transcript
// component, colour comes from the theme rather than from the org's widget
// configuration: this is an audit surface, so the customer sees their brand and
// the investigator sees the same two colours on every conversation.
export const AuditMessage = ({
  component,
}: {
  component: TranscriptComponent;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();

  const markdownOptions: MarkdownToJSX.Options = useMemo(
    () => ({
      overrides: { a: { props: { rel: 'noreferrer', target: '_blank' } } },
      wrapper: 'div',
    }),
    [],
  );

  const messageType = component.message_type;
  const isVisitorFeedback =
    messageType === 'csat_submission' || messageType === 'quick_feedback';
  const isMedia = messageType === 'image' || messageType === 'embed';
  const isAgent = !isVisitorFeedback && component.component_type === 'output';
  // Upload summaries only ride on visitor query (text input) components.
  const attachments =
    messageType === 'text'
      ? (component as TextTranscriptComponent).attachments
      : undefined;

  const bubble = (content: ReactNode) => (
    <Box
      sx={{
        // Recorded messages arrive as HTML whose <p> margins would
        // inflate the bubble.
        '& p': { m: 0 },
        bgcolor: isAgent ? palette.colors.blue[200] : palette.colors.grey[100],
        borderRadius: '16px',
        overflowWrap: 'anywhere',
        px: '14px',
        py: '10px',
      }}
    >
      {content}
    </Box>
  );

  let body: ReactNode = null;
  if (messageType === 'button_selection') {
    body = (
      <ButtonSelectionAnswer
        component={component as ButtonSelectionTranscriptComponent}
      />
    );
  } else if (messageType === 'csat_submission') {
    body = bubble(
      <CsatAnswer component={component as CsatSubmissionTranscriptComponent} />,
    );
  } else if (messageType === 'quick_feedback') {
    body = bubble(
      <QuickFeedbackAnswer
        component={component as QuickFeedbackTranscriptComponent}
      />,
    );
  } else if (messageType === 'image') {
    body = (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ImageStep
          transcriptComponent={component as ImageTranscriptComponent}
        />
      </Box>
    );
  } else if (messageType === 'embed') {
    body = (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <EmbedStep
          transcriptComponent={component as EmbedTranscriptComponent}
        />
      </Box>
    );
  } else {
    const { message, should_render_as_markdown: shouldRenderAsMarkdown } =
      component as TextTranscriptComponent;
    if (message) {
      body = bubble(
        <Typography variant='font14'>
          {shouldRenderAsMarkdown ? (
            <Markdown options={markdownOptions}>{message}</Markdown>
          ) : (
            <Box
              component='span'
              dangerouslySetInnerHTML={{ __html: safeLinkifyHtml(message) }}
              sx={{ whiteSpace: 'pre-wrap' }}
            />
          )}
        </Typography>,
      );
    }
  }

  if (!body && !attachments?.length) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        maxWidth: '85%',
        // Media sizes itself to its container, so it needs a width to fill.
        width: isMedia ? '85%' : undefined,
        ...(isAgent ? { alignSelf: 'flex-start' } : { alignSelf: 'flex-end' }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '4px',
          justifyContent: isAgent ? 'flex-start' : 'flex-end',
          pl: isAgent ? '32px' : 0,
        }}
      >
        <Typography variant='font12Medium'>
          {isAgent
            ? t('conversationAudit.speakerAgent')
            : t('conversationAudit.speakerVisitor')}
        </Typography>
        <Typography color={palette.colors.grey[400]} variant='font12'>
          ·
        </Typography>
        <Typography color={palette.colors.grey[500]} variant='font12'>
          {formatEventTime(component.timestamp)}
        </Typography>
      </Box>
      {messageType === 'button_selection' &&
        (component as ButtonSelectionTranscriptComponent)
          .from_initial_intent_id && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography color={palette.colors.grey[600]} variant='font12'>
              {t('buttonSelection.initialIntent')}
            </Typography>
          </Box>
        )}
      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flexDirection: isAgent ? 'row' : 'row-reverse',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: isAgent ? palette.colors.black : palette.colors.grey[500],
            borderRadius: '50%',
            display: 'flex',
            flexShrink: 0,
            height: '24px',
            justifyContent: 'center',
            width: '24px',
          }}
        >
          {isAgent ? (
            <IconRobot color={palette.colors.white} size={14} />
          ) : (
            <IconUser color={palette.colors.white} size={14} />
          )}
        </Box>
        {body}
      </Box>
      {attachments?.length ? (
        <AttachmentSummaries attachments={attachments} isAgent={isAgent} />
      ) : null}
    </Box>
  );
};
